import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../Supabase/supabaseRealtimeClient';
import { notificationService } from '../services/NotificationService';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import { AccountCircle, Notifications as NotificationsIcon, Menu as MenuIcon, People as FamilyIcon, Dashboard as AdminIcon, Person as ProfileIcon, Home as HomeIcon, CreditCard as CreditCardIcon } from '@mui/icons-material';
import ChamadoDialog, { Chamado } from '../cliente/ChamadoDialog';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const Navbar: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chamadoDialogOpen, setChamadoDialogOpen] = useState(false);
  const [chamadoData, setChamadoData] = useState<Chamado | undefined>(undefined);
  const [hasNewChamado, setHasNewChamado] = useState(false);

  const isLoggedIn = Boolean(localStorage.getItem('userToken'));
  const location = useLocation();
  const isHome = location && location.pathname === '/';
  const navigate = useNavigate();
  const isCliente = localStorage.getItem('userType') === 'cliente';
  const isDependente = localStorage.getItem('userType') === 'dependente';
  const isAdmin = localStorage.getItem('userType') === 'administrador' || localStorage.getItem('userType') === 'socorrista';
  const tipoPlano = localStorage.getItem('tipoPlano');

  // Inicializar serviço de notificação global para admins
  useEffect(() => {
    if (isAdmin) {
      console.log('👨‍💼 Iniciando monitoramento de chamados para admin');
      
      // Aguardar um pouco para garantir que tudo está carregado
      setTimeout(() => {
        notificationService.startGlobalListening();
      }, 2000);
    }

    // Cleanup quando sair
    return () => {
      if (isAdmin) {
        notificationService.stopGlobalListening();
      }
    };
  }, [isAdmin]);

  useEffect(() => {
    const handleNovaNotificacao = () => {
      console.log('🔔 Navbar: Evento novaNotificacao recebido');
      console.log('👤 Tipo de usuário:', localStorage.getItem('userType'), '| É admin?', isAdmin);
      
      setHasNewChamado(true);
      
      // Só tocar som para admins/socorristas
      if (isAdmin) {
        console.log('🔊 Tocando som de notificação para admin/socorrista');
        
        // Criar e configurar áudio
        const audio = new Audio('/assets/notification.mp3');
        audio.volume = 0.7;
        audio.preload = 'auto';
        
        // Tentar tocar
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('✅ Som tocado com sucesso');
            })
            .catch((error) => {
              console.warn('❌ Erro ao tocar MP3:', error);
              
              // Fallback: Web Audio API
              try {
                console.log('🔄 Tentando fallback com Web Audio API');
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                
                if (audioContext.state === 'suspended') {
                  audioContext.resume();
                }
                
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                oscillator.type = 'sine';
                gainNode.gain.setValueAtTime(0, audioContext.currentTime);
                gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
                
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + 0.4);
                
                console.log('✅ Fallback audio tocado');
              } catch (fallbackError) {
                console.error('❌ Erro no fallback:', fallbackError);
              }
            });
        }
      } else {
        console.log('📱 Som não tocado - usuário não é admin');
      }
    };

    const handleDesativarNotificacao = () => {
      setHasNewChamado(false);
    };

    window.addEventListener('novaNotificacao', handleNovaNotificacao);
    window.addEventListener('desativarNotificacao', handleDesativarNotificacao);

    return () => {
      window.removeEventListener('novaNotificacao', handleNovaNotificacao);
      window.removeEventListener('desativarNotificacao', handleDesativarNotificacao);
    };
  }, [isAdmin]);

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  const handleNavigate = (path: string, state?: any) => {
    navigate(path, state ? { state } : undefined);
    setDrawerOpen(false);
  };

  const handleOpenChamadoDialog = () => {
    setChamadoData({
      id: 123,
      cliente_id: 1,
      localizacao: '-23.5505,-46.6333',
      endereco_textual: 'Av. Paulista, 1000, São Paulo - SP',
      status: 'Pendente',
      criado_em: new Date().toISOString(),
    });
    setChamadoDialogOpen(true);
  };

  const handleCloseChamadoDialog = () => setChamadoDialogOpen(false);

  // Função para limpar todos os storages (web e mobile)
  const clearAllStorage = async () => {
    localStorage.clear();
    if (Capacitor.isNativePlatform()) {
      try {
        await Preferences.clear();
      } catch (error) {
        console.error('Erro ao limpar Preferences:', error);
      }
    }
  };

  return (
    <>
      <AppBar 
        position="fixed" 
        color="default" 
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          paddingTop: 'env(safe-area-inset-top)',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.5 },
            '100%': { opacity: 1 }
          }
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0 }}>
            <img 
              src={require('../assets/cropped_image.png')} 
              alt="ALO SUPPORT" 
              style={{ height: 48 }} 
            />
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 'bold',
                color: '#000000',
                whiteSpace: 'nowrap',
                ml: 0.5
              }}
            >
              Saúde 24horas
            </Typography>
          </Link>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            {hasNewChamado && (
              <IconButton
                size="large"
                edge="end"
                aria-label="notifications"
                onClick={() => {
                  setHasNewChamado(false);
                  // Disparar evento global para sincronizar com outros componentes
                  const event = new CustomEvent('desativarNotificacao');
                  window.dispatchEvent(event);
                }}
                sx={{ 
                  color: '#f44336',
                  mr: 1,
                  animation: 'pulse 1.5s infinite'
                }}
              >
                <Badge color="error" variant="dot">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            <IconButton
              size="large"
              edge="end"
              aria-label="menu"
              sx={{ color: '#757575', ml: 1 }}
              onClick={handleDrawerOpen}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerClose}>
        <Box
          // sx={{
          //   width: 250,
          //   height: '100%',
          //   display: 'flex',
          //   flexDirection: 'column',
          //   justifyContent: 'space-between'
          // }}
          sx={{
            height: '100%',
            pt: `calc(env(safe-area-inset-top))`, // altura navbar + safe area top
            pb: 'env(safe-area-inset-bottom)',          // safe area bottom (se precisar)
            px: { xs: 2, sm: 3 },
            bgcolor: 'background.default',
            width: 250,
          }}
          role="presentation"
          onClick={handleDrawerClose}>
          {/* Planos Básico e Premium — Home + Carteirinha */}
          {isCliente && (tipoPlano === 'basico' || tipoPlano === 'premium') && (
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate('/home')}
                  selected={location.pathname === '/home'}
                >
                  <ListItemIcon>
                    <HomeIcon sx={{ color: location.pathname === '/home' ? '#1976d2' : 'inherit' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Home"
                    primaryTypographyProps={{
                      fontWeight: location.pathname === '/home' ? 600 : 400,
                      color: location.pathname === '/home' ? '#1976d2' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate('/carteirinha')}
                  selected={location.pathname === '/carteirinha'}
                >
                  <ListItemIcon>
                    <CreditCardIcon
                      sx={{ color: location.pathname === '/carteirinha' ? '#1976d2' : 'inherit' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Minha Carteirinha"
                    primaryTypographyProps={{
                      fontWeight: location.pathname === '/carteirinha' ? 600 : 400,
                      color: location.pathname === '/carteirinha' ? '#1976d2' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          )}

          {/* Plano Saúde 24h — menu completo */}
          {isCliente && tipoPlano === 'saude24h' && (
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate('/home')}
                  selected={location.pathname === '/home'}
                >
                  <ListItemIcon>
                    <HomeIcon sx={{ color: location.pathname === '/home' ? '#1976d2' : 'inherit' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Home"
                    primaryTypographyProps={{
                      fontWeight: location.pathname === '/home' ? 600 : 400,
                      color: location.pathname === '/home' ? '#1976d2' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate('/family')}
                  selected={location.pathname === '/family'}
                >
                  <ListItemIcon>
                    <FamilyIcon sx={{ color: location.pathname === '/family' ? '#1976d2' : 'inherit' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Familiares"
                    primaryTypographyProps={{
                      fontWeight: location.pathname === '/family' ? 600 : 400,
                      color: location.pathname === '/family' ? '#1976d2' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate('/chamado-cliente')}
                  selected={location.pathname === '/chamado-cliente'}
                >
                  <ListItemIcon>
                    <AdminIcon sx={{ color: location.pathname === '/chamado-cliente' ? '#1976d2' : 'inherit' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Histórico de chamados"
                    primaryTypographyProps={{
                      fontWeight: location.pathname === '/chamado-cliente' ? 600 : 400,
                      color: location.pathname === '/chamado-cliente' ? '#1976d2' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate('/carteirinha')}
                  selected={location.pathname === '/carteirinha'}
                >
                  <ListItemIcon>
                    <CreditCardIcon
                      sx={{ color: location.pathname === '/carteirinha' ? '#1976d2' : 'inherit' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Minha Carteirinha"
                    primaryTypographyProps={{
                      fontWeight: location.pathname === '/carteirinha' ? 600 : 400,
                      color: location.pathname === '/carteirinha' ? '#1976d2' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          )}
          {isDependente && (
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate('/carteirinha')}
                  selected={location.pathname === '/carteirinha'}
                >
                  <ListItemIcon>
                    <CreditCardIcon
                      sx={{ color: location.pathname === '/carteirinha' ? '#1976d2' : 'inherit' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Minha Carteirinha"
                    primaryTypographyProps={{
                      fontWeight: location.pathname === '/carteirinha' ? 600 : 400,
                      color: location.pathname === '/carteirinha' ? '#1976d2' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          )}
          <Box sx={{ py: 2, borderTop: '1px solid #e0e0e0' }}>
            <ListItem disablePadding>
              <ListItemButton onClick={async () => {
                await clearAllStorage();
                navigate('/');
              }}>
                <ListItemIcon><AccountCircle /></ListItemIcon>
                <ListItemText primary="Sair" />
              </ListItemButton>
            </ListItem>
          </Box>
        </Box>
      </Drawer>
      <ChamadoDialog
        open={chamadoDialogOpen}
        onClose={handleCloseChamadoDialog}
        initialData={chamadoData}
        onSave={() => setChamadoDialogOpen(false)}
        isEditing={false}
      />
    </>
  );
};

export default Navbar; 
